<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :show-close="false"
    width="560px"
    center
    append-to-body
    :lock-scroll="false"
    custom-class="login-dialog"
    @closed="handleClosed"
  >
    <div class="login-dialog__panel">
      <div class="login-dialog__top">
        <div class="login-dialog__brand">
          <img src="@/assets/foot-logo.png" alt="江南大学" class="login-dialog__brand-logo">
        </div>
        <button type="button" class="login-dialog__close" aria-label="关闭" @click="dialogVisible = false">
          <i class="el-icon-close" />
        </button>
      </div>

      <h2 class="login-dialog__title">欢迎登录</h2>

      <div class="login-dialog__tabs">
        <button
          type="button"
          class="login-dialog__tab"
          :class="{ 'login-dialog__tab--active': activeTab === 'unified' }"
          @click="activeTab = 'unified'"
        >
          统一登录
        </button>
        <button
          type="button"
          class="login-dialog__tab"
          :class="{ 'login-dialog__tab--active': activeTab === 'account' }"
          @click="activeTab = 'account'"
        >
          账号登录
        </button>
      </div>

      <div v-if="activeTab === 'unified'" class="login-dialog__unified">
        <p class="login-dialog__unified-tip">统一登录功能即将开放，请使用账号登录。</p>
        <button type="button" class="login-dialog__submit" @click="activeTab = 'account'">
          使用账号登录
        </button>
      </div>

      <el-form
        v-else
        ref="formRef"
        :model="form"
        :rules="rules"
        class="login-dialog__form"
        @submit.native.prevent="handleSubmit"
      >
        <el-form-item prop="account">
          <el-input
            v-model="form.account"
            placeholder="请输入账号"
            prefix-icon="el-icon-user"
          />
        </el-form-item>

        <el-form-item prop="password">
          <el-input
            v-model="form.password"
            :type="passwordVisible ? 'text' : 'password'"
            placeholder="请输入密码"
            prefix-icon="el-icon-lock"
            @keyup.enter.native="handleSubmit"
          >
            <i
              slot="suffix"
              class="login-dialog__eye el-icon-view"
              @click="passwordVisible = !passwordVisible"
            />
          </el-input>
        </el-form-item>

        <button
          type="button"
          class="login-dialog__submit"
          :disabled="submitting"
          @click="handleSubmit"
        >
          {{ submitting ? '登录中...' : '登录' }}
        </button>
      </el-form>

      <div class="login-dialog__footer">
        <button type="button" class="login-dialog__footer-link" disabled>注册</button>
        <button type="button" class="login-dialog__footer-link" disabled>忘记密码</button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { login } from '@/api/auth'

export default {
  name: 'LoginDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeTab: 'account',
      submitting: false,
      passwordVisible: false,
      form: {
        account: '',
        password: ''
      },
      rules: {
        account: [{ required: true, message: '请输入账号', trigger: 'blur' }],
        password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
      }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    dialogVisible(visible) {
      this.setBodyScrollLock(visible)
    }
  },
  beforeDestroy() {
    this.setBodyScrollLock(false)
  },
  methods: {
    setBodyScrollLock(locked) {
      document.body.style.overflow = locked ? 'hidden' : ''
      if (!locked) {
        document.body.style.paddingRight = ''
      }
    },
    handleClosed() {
      this.setBodyScrollLock(false)
      this.activeTab = 'account'
      this.passwordVisible = false
      this.form = { account: '', password: '' }
      if (this.$refs.formRef) {
        this.$refs.formRef.resetFields()
      }
    },
    handleSubmit() {
      if (!this.$refs.formRef) return

      this.$refs.formRef.validate((valid) => {
        if (!valid) return

        this.submitting = true
        login({
          account: this.form.account.trim(),
          password: this.form.password
        })
          .then((res) => {
            if (res && res.code === 2000 && res.items) {
              this.$store.dispatch('user/login', res.items)
              this.$message.success('登录成功')
              this.dialogVisible = false
            } else {
              this.$message.error((res && res.message) || '登录失败，请检查账号密码')
            }
          })
          .catch(() => {})
          .finally(() => {
            this.submitting = false
          })
      })
    }
  }
}
</script>

<style lang="scss">
.v-modal {
  background-color: rgba(61, 43, 40, 0.72) !important;
  backdrop-filter: blur(4px);
}

.login-dialog {
  background: transparent;
  box-shadow: none;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
  }
}
</style>

<style lang="scss" scoped>
.login-dialog {
  &__panel {
    position: relative;
    padding: $spacing-3xl $spacing-2xl $spacing-2xl;
    background:
      url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"),
      linear-gradient(160deg, rgba($color-brown-dark, 0.98) 0%, rgba($primary-color, 0.96) 50%, rgba($color-brown-footer, 0.98) 100%);
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.4);

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background:
        radial-gradient(ellipse at 30% 20%, rgba($color-paper, 0.08) 0%, transparent 60%),
        radial-gradient(ellipse at 80% 80%, rgba($success-color, 0.06) 0%, transparent 50%);
      pointer-events: none;
    }
  }

  &__top {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $spacing-xl;
  }

  &__brand-logo {
    width: 180px;
    height: 54px;
    object-fit: contain;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    color: rgba($color-white, 0.7);
    font-size: rem(18);
    transition: all 0.2s;

    &:hover {
      color: $color-white;
      background: rgba($color-white, 0.12);
    }
  }

  &__title {
    position: relative;
    margin: 0 0 $spacing-xl;
    font-family: $font-serif;
    font-size: rem(32);
    font-weight: 700;
    text-align: center;
    color: $color-white;
    letter-spacing: 4px;
    line-height: 1.3;
  }

  &__tabs {
    position: relative;
    display: flex;
    gap: $spacing-2xl;
    justify-content: center;
    margin-bottom: $spacing-xl;
    border-bottom: 1px solid rgba($color-white, 0.15);
  }

  &__tab {
    position: relative;
    padding: $spacing-sm 0 $spacing-md;
    font-size: rem(15);
    color: rgba($color-white, 0.65);
    background: transparent;
    transition: color 0.2s;

    &--active {
      color: $color-white;
      font-weight: 600;

      &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: -1px;
        height: 3px;
        background: $success-color;
        border-radius: 2px;
      }
    }

    &:hover {
      color: rgba($color-white, 0.9);
    }
  }

  &__form {
    position: relative;

    ::v-deep .el-form-item {
      margin-bottom: $spacing-base;
    }

    ::v-deep .el-form-item__error {
      color: $color-white;
      padding-left: $spacing-base;
    }

    ::v-deep .el-input__inner {
      height: 44px;
      line-height: 44px;
      background: $color-white;
      border: none;
      border-radius: 6px;
      color: $color-text;
      box-shadow: none;
    }

    ::v-deep .el-input__prefix {
      color: $color-text-light;
    }
  }

  &__eye {
    cursor: pointer;
    color: $color-text-light;
    line-height: 44px;
    margin-right: 8px;
  }

  &__unified {
    position: relative;
    padding: $spacing-2xl 0;
    text-align: center;
  }

  &__unified-tip {
    margin: 0 0 $spacing-xl;
    color: rgba($color-white, 0.75);
    font-size: rem(14);
    line-height: 1.6;
  }

  &__submit {
    position: relative;
    display: block;
    width: 100%;
    margin-top: $spacing-md;
    padding: $spacing-md;
    background: $success-color;
    color: $color-white;
    font-size: rem(16);
    letter-spacing: 2px;
    border-radius: 6px;
    transition: background 0.25s $ease-apple, opacity 0.2s;

    &:hover:not(:disabled) {
      background: $color-green-light;
    }

    &:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }
  }

  &__footer {
    position: relative;
    display: flex;
    justify-content: space-between;
    margin-top: $spacing-xl;
    padding-top: $spacing-md;
  }

  &__footer-link {
    font-size: rem(14);
    color: rgba($color-white, 0.65);
    background: transparent;

    &:disabled {
      cursor: default;
      opacity: 0.55;
    }
  }
}

@media (max-width: 680px) {
  .login-dialog {
    &__panel {
      padding: $spacing-2xl $spacing-base $spacing-base;
    }

    &__title {
      font-size: rem(26);
    }
  }
}
</style>
